/**
 * ChartFrame — fluid-height wrapper around {@link SafeResponsiveContainer}.
 *
 * Report charts previously pinned a fixed pixel height (`style={{ height: 300 }}`
 * + `<SafeResponsiveContainer height="100%">`). At narrow widths that left a
 * squat, letter-boxed chart with wasted vertical band; at wide widths the chart
 * looked starved. ChartFrame instead reserves a height derived from an aspect
 * ratio of the OBSERVED width, clamped between a min and max, so the chart
 * grows/shrinks fluidly with the column it lives in (kills the wasted-space
 * defect) while never collapsing or ballooning.
 *
 * It deliberately wraps `SafeResponsiveContainer` (NOT recharts'
 * `ResponsiveContainer`) so the >= 8px mount gate / -1×-1 measurement guard is
 * PRESERVED — the inner recharts container still only mounts once the wrapper
 * has real layout. The outer frame measures width via ResizeObserver and
 * computes the height; SafeResponsiveContainer then fills `height="100%"`.
 *
 * Universal layout fix (NOT theme-gated): it changes chart sizing only, never
 * theme tokens, so legacy (no-preset) reports keep identical colours/fonts.
 */
import React from 'react';
import { SafeResponsiveContainer } from './SafeResponsiveContainer';
import { ChartShrinkContext } from '../../lib/ChartShrinkContext';

type SafeProps = React.ComponentProps<typeof SafeResponsiveContainer>;

/**
 * RRQ — is this frame inside a paginated/print/export context where its
 * ancestor host may be clipped to ~0 (so the measurement gate would starve)?
 * In those contexts we mount the inner recharts container EAGERLY at the
 * reserved pixel height instead of waiting for a live measurement that never
 * lands — fixing the legend-only charts in the in-app deck, the /print export
 * and the server Puppeteer PDF.
 *
 * Detected from the live DOM at mount via stable markers that all three
 * surfaces stamp:
 *   - `[data-report-paged]`  — the on-screen Paged.js deck source/clone.
 *   - `[data-print-frame]`   — the /print client export shell.
 *   - `[data-theme-provider="report-print"]` — the print wrapper.
 *   - `[data-report-root][data-print-export]` — the viewMode==='export' root
 *     (PrintReportView / server PDF render the report-root with this flag).
 *   - `[data-paged-chrome-host="true"]` — the OFFSCREEN pagination SOURCE host
 *     PaginatedReportView flows the deck from, present at chart-mount time on
 *     BOTH the on-screen 'view' surface AND the /print surface (it wraps the
 *     source SiteRagReportView whenever paged mode is requested).
 *
 *     RADAR-VIEW-1 (2026-07-04) — the fix for a VIEW↔PDF divergence: on the
 *     'view' surface this source host stays in EDITOR mode (NO
 *     `data-print-export`, and `data-report-paged` is stamped only AFTER the
 *     initial chart mount), so WITHOUT this marker the source's charts took the
 *     measure-first path. The Feasibility radar (the only chart with polar
 *     `cx/cy=50%` geometry) then collapsed to a 0-radius dead centre whenever
 *     recharts' responsive container momentarily measured 0 width, so it never
 *     rasterised cleanly and the LIVE SVG survived into the deck — where pagedjs
 *     dropped the exhibit as the section fragmented (radar + legend + findings
 *     BLANK in the view, fine in the PDF). The /print surface never hit this
 *     because `data-print-export` already made it an export context (eager mount
 *     at a PINNED px width → clean SVG → clean raster `<img>`). Matching the
 *     chrome-host here makes the VIEW source rasterise EAGERLY at a pinned width
 *     exactly like the print source, so BOTH decks carry the same flat chart
 *     `<img>` — one source, true parity.
 *
 *     This marker only exists in the paged path (PaginatedReportView renders it
 *     only when `pagedModeRequested`, which requires a real layout environment —
 *     never jsdom / SSR), so every unit/parity test that renders a chart
 *     component directly is byte-stable (the marker is absent ⇒ measure-first).
 * On the interactive continuous viewer NONE match ⇒ the measure-first guard is
 * unchanged (byte-stable).
 */
function isExportContext(el: Element | null): boolean {
  if (!el || typeof el.closest !== 'function') return false;
  return el.closest(
    '[data-report-paged], [data-print-frame], [data-theme-provider="report-print"], [data-report-root][data-print-export], [data-paged-chrome-host="true"]',
  ) != null;
}

interface ChartFrameProps
  extends Omit<SafeProps, 'height' | 'width'> {
  /** Height : width ratio used to derive the fluid height from the
   *  observed width. 0.5 ⇒ height is half the width (a 2:1 landscape
   *  chart). Defaults to 0.55. */
  aspect?: number;
  /** Lower clamp (px) on the derived height — chart never gets squatter. */
  minHeight?: number;
  /** Upper clamp (px) on the derived height — chart never balloons on a
   *  very wide desktop column. */
  maxHeight?: number;
  /**
   * SHRINK-TO-FIT (RC-2) — an OPTIONAL tighter upper clamp the paginator can
   * pass to shrink a chart DOWN so it fills the remaining space on a page foot
   * instead of being pushed whole to the next page (the white-gap defect).
   * When set, the effective max is `min(maxHeight, max(fitMaxHeight,
   * legibilityFloor))` — i.e. it can lower the reserved height but NEVER below
   * `legibilityFloor` (axis ticks/labels stay readable; we shrink plot slack,
   * not type). Absent ⇒ behaviour is byte-stable (uses `maxHeight`).
   */
  fitMaxHeight?: number;
  /**
   * SHRINK-TO-FIT (RC-2) — the HARD legibility floor (px) `fitMaxHeight` may
   * approach but never cross. Defaults to `minHeight` when omitted. NEVER
   * implemented via `transform: scale()` — a transform shrinks text below the
   * floor AND leaves the layout box unchanged (so it would not even close the
   * gap, and the raster captures the box rect). This is a pure height clamp.
   */
  legibilityFloor?: number;
  /** Optional className on the outer measured frame. */
  frameClassName?: string;
  /** Optional style on the outer measured frame (e.g. maxWidth). */
  frameStyle?: React.CSSProperties;
  /**
   * Optional `data-export-id` of the parent capture box. When provided,
   * `ChartFrame` will read its RC-2 pass 2 shrink constraint from
   * `ChartShrinkContext`.
   */
  exportId?: string;
}

/** Default fluid bounds — tuned to the report's body column width so the
 *  common case (≈ 640px column) lands near the prior fixed 300px height. */
const DEFAULT_ASPECT = 0.55;
const DEFAULT_MIN_HEIGHT = 240;
const DEFAULT_MAX_HEIGHT = 420;

export const ChartFrame: React.FC<ChartFrameProps> = ({
  aspect = DEFAULT_ASPECT,
  minHeight = DEFAULT_MIN_HEIGHT,
  maxHeight = DEFAULT_MAX_HEIGHT,
  fitMaxHeight: overrideFitMaxHeight,
  legibilityFloor,
  frameClassName,
  frameStyle,
  exportId,
  children,
  ...safeProps
}) => {
  const shrinkMap = React.useContext(ChartShrinkContext);
  const contextFit = exportId ? shrinkMap[exportId] : undefined;
  const fitMaxHeight = overrideFitMaxHeight ?? contextFit;

  const frameRef = React.useRef<HTMLDivElement | null>(null);
  // Start at minHeight so the first paint reserves usable space (and the
  // inner SafeResponsiveContainer's mount-gate sees a non-zero height
  // immediately on the common already-laid-out path).
  const [height, setHeight] = React.useState<number>(minHeight);
  // RRQ — resolved once at mount: is this frame inside a paged/print/export
  // surface (where the host can be clipped to ~0)? When so we mount the inner
  // recharts container eagerly at the reserved px height. Default false ⇒ the
  // interactive viewer keeps the measure-first guard.
  const [exportContext, setExportContext] = React.useState(false);
  // RRQ CHARTS FIX (A) — the CONCRETE measured frame width (px). In the
  // export/print context we hand this fixed number to SafeResponsiveContainer
  // instead of width='100%' so the inner Recharts container computes its
  // scales / clip-path / series geometry against a PINNED width that does not
  // change when `page.pdf()` reflows. With Fix (B) the export root is already
  // capped to the printable A4 column at screen-media load, so this width is
  // the FINAL print width (≈688px single-col / ≈half for a 2-col exhibit) and
  // nothing is clipped. Null until first measure ⇒ the interactive (non-export)
  // path always uses '100%' and is byte-stable.
  const [measuredWidth, setMeasuredWidth] = React.useState<number | null>(null);

  React.useEffect(() => {
    const el = frameRef.current;
    if (!el) return;

    setExportContext(isExportContext(el));

    const recompute = (width: number) => {
      if (!(width > 0)) return;
      setMeasuredWidth((prev) => (prev === width ? prev : width));
      // SHRINK-TO-FIT (RC-2) — when the paginator passes a `fitMaxHeight`, lower
      // the effective upper clamp to fit the remaining page space, but FLOOR it
      // at the legibility bound (default minHeight) so the chart never crushes
      // its axes/labels. Absent ⇒ `effMax === maxHeight` (byte-stable).
      const floor = legibilityFloor ?? minHeight;
      const effMax =
        fitMaxHeight != null
          ? Math.min(maxHeight, Math.max(fitMaxHeight, floor))
          : maxHeight;
      const next = Math.round(
        Math.min(effMax, Math.max(minHeight, width * aspect)),
      );
      setHeight((prev) => (prev === next ? prev : next));
    };

    recompute(el.getBoundingClientRect().width);

    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      recompute(entry.contentRect.width);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [aspect, minHeight, maxHeight, fitMaxHeight, legibilityFloor]);

  return (
    <div
      ref={frameRef}
      className={frameClassName}
      style={{ width: '100%', height, ...frameStyle }}
      data-testid="chart-frame"
      data-export-id={exportId}
      data-chart-legibility-floor={legibilityFloor ?? minHeight}
      data-chart-max-height={maxHeight}
    >
      {/* RRQ — pass the resolved numeric `height` (not '100%') so:
           (a) in an export/paged/print context the inner container mounts
               EAGERLY at this reserved box even when an ancestor clips the
               live rect to ~0 (kills the legend-only chart); and
           (b) on the interactive viewer the measurement-gate fallback has a
               concrete height to reserve if the live measurement ever stalls.
           The frame's own box is still `height` px, so the chart fills it
           identically to the prior `height="100%"`. */}
      <SafeResponsiveContainer
        // RRQ CHARTS FIX (A) — in the export/print context pass the CONCRETE
        // measured frame width (px) so Recharts pins its geometry to the final
        // print width; elsewhere keep '100%' (byte-stable interactive viewer).
        // Falls back to '100%' until the first measurement lands.
        width={exportContext && measuredWidth != null ? measuredWidth : '100%'}
        height={height}
        eager={exportContext}
        {...safeProps}
      >
        {children as React.ReactElement}
      </SafeResponsiveContainer>
    </div>
  );
};
